import { OASElement } from '@oas-ui/core'
import { isRtl } from '../../shared/direction.js'

const STYLE = `
:host {
  display: block;
  font-family: inherit;
  position: relative;
  overflow: hidden;
  background: var(--oas-color-bg-hover);
  border-radius: var(--oas-radius-lg);
}
:host([hidden]) {
  display: none;
}
.viewport {
  overflow: hidden;
  /* 水平模式放行纵向页面滚动，拖拽手势只接管横向 */
  touch-action: pan-y;
}
:host([direction='vertical']) .viewport {
  touch-action: pan-x;
}
.track {
  display: flex;
  transition: transform var(--oas-transition-base) var(--oas-ease-out);
}
/* 拖拽跟手期间关闭过渡（松手后移除类，回弹/切屏恢复过渡） */
.track.no-transition {
  transition: none;
}
::slotted(*) {
  flex: 0 0 100%;
  width: 100%;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
}
:host([direction='vertical']) .track {
  flex-direction: column;
  /* 垂直模式视口需定高（默认 200px，与 min-height 约定对齐），可用 --oas-carousel-height 覆盖 */
  height: 100%;
}
:host([direction='vertical']) .viewport {
  height: var(--oas-carousel-height, 200px);
}
:host([direction='vertical']) ::slotted(*) {
  flex: 0 0 100%;
  height: 100%;
  min-height: 0;
}
/* 淡出淡入：全部轮播项叠在同一网格单元（in-flow 堆叠，容器高度取最高屏） */
:host([effect='fade']) .track {
  display: grid;
}
:host([effect='fade']) ::slotted(*) {
  grid-area: 1 / 1;
  transition: opacity var(--oas-transition-base) var(--oas-ease-out);
}
/* 卡片模式：当前卡居中为主体，左右邻卡部分露出并缩小降透明（仅水平方向生效） */
:host([type='card']) .track {
  gap: var(--oas-carousel-card-gap, var(--oas-space-3));
}
:host([type='card']) ::slotted(*) {
  flex: 0 0 var(--oas-carousel-card-width, 60%);
  width: var(--oas-carousel-card-width, 60%);
  transition:
    transform var(--oas-transition-base) var(--oas-ease-out),
    opacity var(--oas-transition-base) var(--oas-ease-out);
  cursor: pointer;
}
/* 显式暂停按钮：固定在右上角，图标随 aria-pressed 切换（内联原创 SVG） */
.pause-btn {
  position: absolute;
  top: var(--oas-space-3);
  right: var(--oas-space-3);
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--oas-control-height-md);
  height: var(--oas-control-height-md);
  padding: 0;
  border: 1px solid var(--oas-color-border);
  border-radius: 50%;
  background: var(--oas-color-bg);
  color: var(--oas-color-text-primary);
  font-family: inherit;
  cursor: pointer;
  opacity: 0.85;
  transition:
    opacity var(--oas-transition-base) var(--oas-ease-out),
    color var(--oas-transition-base) var(--oas-ease-out),
    border-color var(--oas-transition-base) var(--oas-ease-out),
    background var(--oas-transition-base) var(--oas-ease-out);
}
.pause-btn[hidden] {
  display: none;
}
.pause-btn:hover {
  opacity: 1;
  color: var(--oas-color-primary);
  border-color: var(--oas-color-primary);
  background: var(--oas-color-bg-hover);
}
.pause-btn:focus-visible {
  outline: 2px solid var(--oas-color-primary);
  outline-offset: 2px;
}
.pause-btn svg {
  display: block;
  width: 14px;
  height: 14px;
  fill: currentColor;
}
.pause-btn .icon-play {
  display: none;
}
.pause-btn[aria-pressed='true'] .icon-play {
  display: block;
}
.pause-btn[aria-pressed='true'] .icon-pause {
  display: none;
}
/* 垂直模式：暂停钮挪到左上角（只换 right→left，top 不变；右侧让位给垂直指示器） */
:host([direction='vertical']) .pause-btn {
  right: auto;
  left: var(--oas-space-3);
}
.dots {
  position: absolute;
  bottom: var(--oas-space-3);
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  gap: var(--oas-space-2);
  z-index: 2;
}
.dots[hidden] {
  display: none;
}
/* 指示器外挂（不占轮播盒内空间，撑高宿主） */
.dots.outside {
  position: static;
  margin-top: var(--oas-space-3);
}
.dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: none;
  background: var(--oas-carousel-dot-bg, rgba(255, 255, 255, 0.5));
  cursor: pointer;
  padding: 0;
  transition:
    width var(--oas-transition-base) var(--oas-ease-out),
    height var(--oas-transition-base) var(--oas-ease-out),
    background var(--oas-transition-base) var(--oas-ease-out);
}
.dot:hover {
  background: var(--oas-carousel-dot-active-bg, #ffffff);
}
.dot:focus-visible {
  outline: 2px solid var(--oas-color-primary);
  outline-offset: 2px;
}
.dot[aria-current='true'] {
  background: var(--oas-carousel-dot-active-bg, #ffffff);
}
/* ===== thumbs：缩略图指示器（替代圆点） ===== */
.dots.thumbs {
  gap: var(--oas-space-2);
  max-width: 100%;
  overflow-x: auto;
  padding: 0 var(--oas-space-2);
  box-sizing: border-box;
}
.thumb {
  flex: 0 0 auto;
  width: 56px;
  height: 36px;
  padding: 0;
  border: 2px solid transparent;
  border-radius: var(--oas-radius-sm);
  background: var(--oas-color-bg-hover);
  overflow: hidden;
  cursor: pointer;
  box-sizing: border-box;
  opacity: 0.6;
  transition:
    border-color var(--oas-transition-base) var(--oas-ease-out),
    opacity var(--oas-transition-base) var(--oas-ease-out);
}
.thumb:hover {
  opacity: 1;
}
.thumb:focus-visible {
  outline: 2px solid var(--oas-color-primary);
  outline-offset: 2px;
}
/* 当前项高亮描边（主色 token） */
.thumb[aria-current='true'] {
  border-color: var(--oas-color-primary);
  opacity: 1;
}
.thumb img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.thumb-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-size: var(--oas-font-size-sm);
  color: var(--oas-color-text-secondary);
}
/* 垂直模式：缩略图条竖排（列方向） */
:host([direction='vertical']) .dots.thumbs {
  flex-direction: column;
}
/* RTL 镜像：缩略图条与指示器镜像排列（row 方向随书写方向自动换起始侧），
    暂停钮翻到另一角（物理定位显式镜像） */
:host([data-rtl]) .pause-btn {
  right: auto;
  left: var(--oas-space-3);
}
/* 线性指示器 */
.dots.line .dot {
  width: 20px;
  height: 4px;
  border-radius: var(--oas-radius-sm);
}
.dots.line .dot[aria-current='true'] {
  width: 28px;
}
/* 垂直模式：inside 指示器默认靠右（行业惯例），外挂则居右侧流内 */
:host([direction='vertical']) .dots {
  left: auto;
  right: var(--oas-space-3);
  top: 50%;
  bottom: auto;
  transform: translateY(-50%);
  flex-direction: column;
}
:host([direction='vertical']) .dots.outside {
  position: static;
  transform: none;
  margin-top: 0;
  margin-inline-start: var(--oas-space-3);
}
:host([direction='vertical']) .dots.line .dot {
  width: 4px;
  height: 20px;
}
:host([direction='vertical']) .dots.line .dot[aria-current='true'] {
  width: 4px;
  height: 28px;
}
/* 垂直外挂：宿主改为横向排列（轮播区 + 右侧指示器） */
:host([direction='vertical']:has(.dots.outside)) {
  display: flex;
  align-items: center;
}
/* 触屏（coarse）：命中区抬到 44px 触控目标（--oas-touch-target-min）。
   箭头/暂停钮为绝对定位浮钮，直接放大钮体（图标尺寸不变）；
   指示圆点视觉保持 12px，padding 扩热区 + margin 负补偿不占布局，
   间距同步放大（32px）保证相邻命中区不重叠误触。纯 CSS 增强，DOM 无分叉 */
@media (pointer: coarse) {
  .arrow,
  .pause-btn {
    width: max(var(--oas-control-height-md), var(--oas-touch-target-min, 44px));
    height: max(var(--oas-control-height-md), var(--oas-touch-target-min, 44px));
  }
  .dots {
    gap: 32px;
  }
  .dot {
    box-sizing: content-box;
    padding: 16px;
    margin: -16px;
  }
  /* 线性指示器：按 20×4（水平）/ 4×20（垂直）补足 44px 命中区 */
  .dots.line .dot {
    padding: 20px 12px;
    margin: -20px -12px;
  }
  :host([direction='vertical']) .dots.line .dot {
    padding: 12px 20px;
    margin: -12px -20px;
  }
}
.arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--oas-control-height-md);
  height: var(--oas-control-height-md);
  padding: 0;
  border: 1px solid var(--oas-color-border);
  border-radius: 50%;
  background: var(--oas-color-bg);
  color: var(--oas-color-text-primary);
  font-size: var(--oas-font-size-lg);
  font-family: inherit;
  line-height: 1;
  cursor: pointer;
  /* 默认（悬停形态）：箭头隐藏，悬停/聚焦容器时淡入 */
  opacity: 0;
  pointer-events: none;
  transition:
    opacity var(--oas-transition-base) var(--oas-ease-out),
    color var(--oas-transition-base) var(--oas-ease-out),
    border-color var(--oas-transition-base) var(--oas-ease-out),
    background var(--oas-transition-base) var(--oas-ease-out);
}
:host(:hover) .arrow,
:host(:focus-within) .arrow {
  opacity: 1;
  pointer-events: auto;
}
:host([arrows='always']) .arrow {
  opacity: 1;
  pointer-events: auto;
}
/* hidden 属性需要显式覆盖 display（避免 class 的 display 优先级压过 UA 的 [hidden] 规则） */
.arrow[hidden] {
  display: none;
}
.arrow[disabled] {
  opacity: 0.45;
  pointer-events: none;
  cursor: not-allowed;
}
.arrow:hover {
  opacity: 1;
  color: var(--oas-color-primary);
  border-color: var(--oas-color-primary);
  background: var(--oas-color-bg-hover);
}
.arrow:focus-visible {
  outline: 2px solid var(--oas-color-primary);
  outline-offset: 2px;
}
/* 逻辑属性：prev 在起始侧、next 在尾侧，RTL 自动镜像（视觉上 prev 换到右侧） */
.arrow-prev { inset-inline-start: var(--oas-space-3); }
.arrow-next { inset-inline-end: var(--oas-space-3); }
/* 垂直模式箭头：旋转 90°（‹ 朝上、› 朝下），改到顶部/底部居中 */
:host([direction='vertical']) .arrow {
  inset-inline-start: 50%;
  inset-inline-end: auto;
  transform: translateX(-50%) rotate(90deg);
}
:host([direction='vertical']) .arrow-prev {
  top: var(--oas-space-3);
}
:host([direction='vertical']) .arrow-next {
  top: auto;
  bottom: var(--oas-space-3);
}
`

export class OASCarousel extends OASElement {
  static override get observedAttributes(): string[] {
    return [
      'index',
      'autoplay',
      'interval',
      'arrows',
      'indicators',
      'indicator-position',
      'indicator-type',
      'trigger',
      'effect',
      'direction',
      'type',
      'loop',
      'pause-on-hover',
      'pause-button',
      'slides-per-view',
      'gap',
      'draggable',
      // thumbs：缩略图指示器（替代圆点，取子项 img / data-thumb / thumbs JSON 数组）
      'thumbs',
    ]
  }

  private count = 0
  private timer: ReturnType<typeof setInterval> | null = null
  /** 悬停/聚焦/页面不可见/拖拽/显式暂停 五种暂停源（WCAG 2.2.2：自动播放必须可暂停） */
  private hoverPaused = false
  private focusPaused = false
  private hiddenPaused = false
  private dragPaused = false
  /** 显式暂停（pause-button 触发）：优先级最高，悬停移出不会自动恢复 */
  private userPaused = false
  /** 拖拽进行中的手势状态（null=未拖拽）；startAt 供松手速度计算（轻扫翻页判定） */
  private drag: { startX: number; startY: number; delta: number; startAt: number } | null = null

  /** 纯函数：SSR 快照与客户端渲染共用同一份模板，保证两路径结构严格一致 */
  private template(): string {
    return `
      <style>${STYLE}</style>
      <div class="viewport" part="viewport">
        <div class="track" part="track"><slot></slot></div>
      </div>
      <button type="button" class="arrow arrow-prev" part="arrow-prev" aria-label="">‹</button>
      <button type="button" class="arrow arrow-next" part="arrow-next" aria-label="">›</button>
      <button type="button" class="pause-btn" part="pause-button" aria-label="" hidden>
        <svg class="icon-pause" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M4.5 3h2.4v10H4.5zM9.1 3h2.4v10H9.1z"/></svg>
        <svg class="icon-play" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M5 3.2v9.6L12.4 8z"/></svg>
      </button>
      <div class="dots" part="dots" role="tablist"></div>
    `
  }

  /** 缓存节点引用 + 绑定事件 + 注册清理（render 与水合路径共用） */
  /** 断开重连重绑（core onReconnect 钩子）：bind() 幂等重入——全部委托类字段引用
      （addEventListener 规范去重），cleanup 断开已清空故重新注册 */
  protected override onReconnect(): void {
    this.bind()
  }

  private bind(): void {
    this.shadow.querySelector('.dots')?.addEventListener('click', this.onDotActivate)
    // 指示器触发方式：trigger="hover" 时悬停指示器切页（click 为默认；hover 下 click 仍可用）；
    // 非法值静默回落 click。委托到容器监听（指示器重建无需重绑）
    this.shadow.querySelector('.dots')?.addEventListener('pointerover', this.onDotHover)
    // 指示器键盘导航（WAI-ARIA carousel pattern：方向键 + Home/End；水平轴 RTL 镜像）
    this.shadow.querySelector('.dots')?.addEventListener('keydown', this.onDotsKeydown)
    this.shadow.querySelector('[part="arrow-prev"]')?.addEventListener('click', this.onArrowPrevClick)
    this.shadow.querySelector('[part="arrow-next"]')?.addEventListener('click', this.onArrowNextClick)
    // 显式暂停按钮：autoplay 未开启时点击=开启播放；已开启时切换暂停/继续
    this.shadow.querySelector('[part="pause-button"]')?.addEventListener('click', this.onPauseButtonClick)
    // 卡片模式：点击任一邻卡直接切到该卡（等效多步 next/prev），点击当前卡 no-op
    this.addEventListener('click', this.onHostCardClick)
    // 卡片模式：宿主级方向键切换（焦点在轮播项内时可达；指示器区有独立导航，避免重复处理；水平轴 RTL 镜像）
    this.addEventListener('keydown', this.onHostCardKeydown)
    // 动态增删轮播项：slotchange 重数数量、重建指示器、收敛 index
    this.shadow.querySelector('slot')?.addEventListener('slotchange', this.onSlotChange)
    // 悬停/聚焦暂停自动播放（默认开，pause-on-hover=false 可关）
    this.addEventListener('pointerenter', this.onHoverEnter)
    this.addEventListener('pointerleave', this.onHoverLeave)
    this.addEventListener('focusin', this.onFocusIn)
    this.addEventListener('focusout', this.onFocusOut)
    // 页面不可见停播，可见后恢复；bind 重入（重连）时同步当前可见态——
    // 断开期间 document.hidden 变化后 hiddenPaused 是陈旧值（监听已摘，交叉审 M3）
    this.hiddenPaused = document.hidden
    document.addEventListener('visibilitychange', this.onVisibilityChange)
    this.bindDrag()
    this.onCleanup(() => {
      if (this.timer) clearInterval(this.timer)
      this.timer = null
      document.removeEventListener('visibilitychange', this.onVisibilityChange)
    })
  }

  /** 卡片模式：点击任一邻卡直接切到该卡（等效多步 next/prev），点击当前卡 no-op */
  private onHostCardClick = (e: Event): void => {
    if (!this.isCard()) return
    const path = e.composedPath()
    const kids = Array.from(this.children)
    const hit = kids.findIndex((k) => path.includes(k))
    if (hit >= 0 && hit !== this.current()) this.goTo(hit)
  }

  /** 卡片模式宿主级方向键切换（焦点在轮播项内时可达；指示器区有独立导航避免重复处理；水平轴 RTL 镜像） */
  private onHostCardKeydown = (e: Event): void => {
    if (!this.isCard()) return
    const ev = e as KeyboardEvent
    const dots = this.shadow.querySelector('.dots')
    if (dots && ev.composedPath().includes(dots)) return
    const fwd = isRtl(this) ? 'ArrowLeft' : 'ArrowRight'
    const back = isRtl(this) ? 'ArrowRight' : 'ArrowLeft'
    if (ev.key === fwd || ev.key === 'ArrowDown') {
      ev.preventDefault()
      this.next()
    } else if (ev.key === back || ev.key === 'ArrowUp') {
      ev.preventDefault()
      this.prev()
    }
  }

  /** 指示器键盘导航（WAI-ARIA carousel pattern：方向键 + Home/End；水平轴 RTL 镜像；
      类字段引用——bind 幂等重入不重复挂（匿名期重连双挂连跳多页，交叉审实抓 C1） */
  private onDotsKeydown = (e: Event): void => {
    const key = (e as KeyboardEvent).key
    const pages = this.pageCount()
    const fwd = isRtl(this) ? 'ArrowLeft' : 'ArrowRight'
    const back = isRtl(this) ? 'ArrowRight' : 'ArrowLeft'
    if (key === fwd || key === 'ArrowDown') {
      e.preventDefault()
      this.goTo(this.current() + 1)
    } else if (key === back || key === 'ArrowUp') {
      e.preventDefault()
      this.goTo(this.current() - 1)
    } else if (key === 'Home') {
      e.preventDefault()
      this.goTo(0)
    } else if (key === 'End') {
      e.preventDefault()
      this.goTo(pages - 1)
    }
  }

  /** 指示器激活（click 默认触发；类字段引用——bind 幂等重入不重复挂） */
  private onDotActivate = (e: Event): void => {
    const dot = (e.target as HTMLElement).closest('[part="dot"], [part="thumb"]')
    if (dot) this.goTo(Number((dot as HTMLElement).getAttribute('data-index')) || 0)
  }

  /** 指示器悬停切页（trigger="hover" 时） */
  private onDotHover = (e: Event): void => {
    if (this.getAttr('trigger', 'click') !== 'hover') return
    const dot = (e.target as HTMLElement).closest('[part="dot"], [part="thumb"]')
    if (dot) this.goTo(Number((dot as HTMLElement).getAttribute('data-index')) || 0)
  }

  private onHoverEnter = (): void => {
    this.hoverPaused = true
  }
  private onHoverLeave = (): void => {
    this.hoverPaused = false
    this.schedule()
  }

  /** 箭头/暂停钮/slotchange（类字段引用——bind 幂等重入不重复挂；定点审实抓的残留匿名） */
  private onArrowPrevClick = (): void => {
    this.prev()
  }
  private onArrowNextClick = (): void => {
    this.next()
  }
  private onPauseButtonClick = (): void => {
    if (!this.hasAttr('autoplay')) {
      this.userPaused = false
      // attributeChangedCallback 会同步 update/schedule
      this.setAttribute('autoplay', '')
      return
    }
    this.userPaused = !this.userPaused
    this.update()
    this.schedule()
  }
  private onSlotChange = (): void => {
    this.update()
  }
  private onFocusIn = (): void => {
    this.focusPaused = true
  }
  private onFocusOut = (): void => {
    this.focusPaused = false
    this.schedule()
  }
  private onVisibilityChange = (): void => {
    this.hiddenPaused = document.hidden
    this.schedule()
  }

  /**
   * 拖拽开关：draggable 属性显式开启（"false" 显式关闭，优先级最高）；
   * 未声明时触摸设备（pointer: coarse）默认开、PC 默认关。
   */
  private dragEnabled(): boolean {
    if (this.hasAttr('draggable')) return this.getAttr('draggable', '') !== 'false'
    try {
      return window.matchMedia?.('(pointer: coarse)').matches ?? false
    } catch {
      return false
    }
  }

  /** pointer 拖拽跟手（触摸/鼠标统一）：按下暂停过渡，move 跟手，up 按阈值切屏或回弹 */
  private bindDrag(): void {
    const viewport = this.shadow.querySelector<HTMLElement>('[part="viewport"]')
    if (!viewport) return
    viewport.addEventListener('pointerdown', this.onViewportPointerdown)
    viewport.addEventListener('pointermove', this.onViewportPointermove)
    viewport.addEventListener('pointerup', this.onViewportPointerFinish)
    viewport.addEventListener('pointercancel', this.onViewportPointerFinish)
  }

  /** pointer 拖拽按下（类字段引用——bindDrag 幂等重入不重复挂） */
  private onViewportPointerdown = (e: Event): void => {
    const ev = e as PointerEvent
    if (!this.dragEnabled() || this.drag || ev.button !== 0 || this.count === 0) return
    const viewport = this.shadow.querySelector<HTMLElement>('[part="viewport"]')!
    this.drag = { startX: ev.clientX, startY: ev.clientY, delta: 0, startAt: performance.now() }
    this.dragPaused = true
    trackOf(this)?.classList.add('no-transition')
    try {
      viewport.setPointerCapture(ev.pointerId)
    } catch {
      /* 环境不支持指针捕获时忽略（事件仍挂在 viewport 上） */
    }
  }

  private onViewportPointermove = (e: Event): void => {
    if (!this.drag) return
    const ev = e as PointerEvent
    const vertical = !this.isCard() && this.getAttr('direction', 'horizontal') === 'vertical'
    this.drag.delta = vertical ? ev.clientY - this.drag.startY : ev.clientX - this.drag.startX
    trackOf(this)?.style.setProperty('transform', this.trackTransform(this.drag.delta))
  }

  /** pointer 拖拽收尾（up/cancel 同路径）：阈值切屏或回弹 + 方向 RTL 镜像 */
  private onViewportPointerFinish = (e: Event): void => {
    if (!this.drag) return
    const ev = e as PointerEvent
    const viewport = this.shadow.querySelector<HTMLElement>('[part="viewport"]')!
    const vertical = !this.isCard() && this.getAttr('direction', 'horizontal') === 'vertical'
    const delta = vertical ? this.drag.delta : ev.clientX - this.drag.startX || this.drag.delta
    const elapsed = Math.max(performance.now() - this.drag.startAt, 1)
    this.drag = null
    this.dragPaused = false
    const t = trackOf(this)
    t?.classList.remove('no-transition')
    // 松手阈值：距离 > 视口 25%（水平视宽/垂直视高；无布局量测时回落 50px）
    // 或快速轻扫（≥40px 且速度 > 0.5px/ms）；否则回弹。
    // 方向：LTR 左拖（delta<0）= 下一张；RTL 镜像为右拖（delta>0）= 下一张
    const rect = viewport.getBoundingClientRect()
    const span = Math.max(vertical ? rect.height : rect.width, 0)
    const distance = Math.abs(delta)
    const distanceThreshold = span > 0 ? span * 0.25 : 50
    const flick = distance >= 40 && distance / elapsed > 0.5
    const rtl = !vertical && isRtl(this)
    const forward = rtl ? delta > 0 : delta < 0
    if (distance > distanceThreshold || flick) this.goTo(this.current() + (forward ? 1 : -1))
    // 回弹/边界归位：统一重算 transform（未达阈值或循环关闭触界时恢复原位）
    this.update()
    // 拖拽后重置自动播放计时
    this.schedule()
  }

  protected override render(): void {
    this.shadow.innerHTML = this.template()
    this.bind()
    this.update()
  }

  /** 真水合：校验 SSR 快照结构（视口/轨道存在）后直接接管，跳过 shadow 重建 */
  protected override hydrate(): boolean {
    if (!this.shadow.querySelector('.viewport') || !this.shadow.querySelector('.track')) return false
    this.bind()
    return true
  }

  /** 当前屏（页）索引 */
  private current(): number {
    return Number(this.getAttr('index', '0')) || 0
  }

  /** 每屏轮播项数（fade 与卡片模式均为单卡全宽语义，恒为 1） */
  private perView(): number {
    if (this.isCard()) return 1
    if (this.getAttr('effect', 'slide') === 'fade') return 1
    const n = Math.floor(Number(this.getAttr('slides-per-view', '1')))
    return Number.isFinite(n) && n > 0 ? n : 1
  }

  /** 卡片模式：type="card"，当前卡居中为主体、邻卡两侧露出（仅水平方向生效） */
  private isCard(): boolean {
    return this.getAttr('type', '') === 'card'
  }

  /** 页数 = 按屏步进的组数（指示器个数） */
  private pageCount(): number {
    if (this.count === 0) return 0
    return Math.max(1, Math.ceil(this.count / this.perView()))
  }

  private gapPx(): number {
    const n = Number(this.getAttr('gap', '0'))
    return Number.isFinite(n) && n > 0 ? n : 0
  }

  /**
   * 轨道位移：水平 translateX / 垂直 translateY。
   * 每页步进 perView 个 slide 宽（含 gap）；非整除时末页对齐轨道末尾不溢出。
   * deltaPx 为拖拽跟手偏移（px），非拖拽时为 0。
   */
  private trackTransform(deltaPx = 0): string {
    // RTL 镜像：水平轨道基准位移取 LTR 的精确取反（镜像对称 T_rtl = -T_ltr，center/clamp 项一并取负）。
    // 拖拽跟手 delta 恒取物理原值（手指右移内容必须右移，取反会逆手）；松手阈值方向在 finish 里换向。
    // 垂直轴不受书写方向影响。
    const rtl = this.getAttr('direction', 'horizontal') !== 'vertical' && isRtl(this)
    // 卡片模式仅水平：第 i 卡中心对齐视口中心，左右邻卡自然露出。
    // 非循环模式首尾屏贴边（首屏贴起始侧露邻卡、末屏贴尾侧露邻卡），边界不悬空。
    if (this.isCard()) {
      const w = 'var(--oas-carousel-card-width, 60%)'
      const gap = 'var(--oas-carousel-card-gap, var(--oas-space-3))'
      const i = Math.min(this.current(), Math.max(0, this.count - 1))
      let center = `(100% - (${w})) / 2`
      if (this.getAttr('loop', '') === 'false') {
        if (i === 0) center = '0px'
        else if (i === this.count - 1) center = `(100% - (${w}))`
      }
      const base = rtl ? `${i} * ((${w}) + ${gap}) - (${center})` : `-1 * ${i} * ((${w}) + ${gap}) + (${center})`
      return `translateX(calc(${base} + ${deltaPx}px))`
    }
    const axis = this.getAttr('direction', 'horizontal') === 'vertical' ? 'Y' : 'X'
    const per = this.perView()
    const offsetSlides = Math.min(this.current() * per, Math.max(0, this.count - per))
    // 常规形态（每屏 1 项、无间距）输出最简形式，SSR 快照体积与旧版一致
    if (per === 1 && this.gapPx() === 0) {
      const base = rtl ? `${offsetSlides * 100}%` : `-${offsetSlides * 100}%`
      return deltaPx ? `translate${axis}(calc(${base} + ${deltaPx}px))` : `translate${axis}(${base})`
    }
    const gap = `${this.gapPx()}px`
    const slideW = `((100% - ${per - 1} * ${gap}) / ${per})`
    const base = rtl ? `${offsetSlides} * ((${slideW}) + ${gap})` : `-1 * ${offsetSlides} * ((${slideW}) + ${gap})`
    return `translate${axis}(calc(${base} + ${deltaPx}px))`
  }

  protected override update(): void {
    this.count = this.children.length
    // RTL 镜像开关（缩略图条/暂停钮等处 CSS 消费）
    this.toggleAttribute('data-rtl', isRtl(this))
    const pages = this.pageCount()
    // index 恒反射：缺省归 0 并收敛到有效页范围（children 减少或外部越界设置时）
    if (pages > 0) {
      const raw = this.getAttribute('index')
      const clamped = Math.min(pages - 1, Math.max(0, this.current()))
      if (String(clamped) !== raw) this.setAttribute('index', String(clamped))
    }
    const index = this.current()

    const track = trackOf(this)
    if (track) track.style.transform = this.trackTransform()

    // fade 叠层：激活屏 opacity 1，其余 0（grid 堆叠，in-flow）；卡片模式：当前卡原大，邻卡缩小降透明；
    // slide 模式清理内联态
    const fade = !this.isCard() && this.getAttr('effect', 'slide') === 'fade'
    const card = this.isCard()
    Array.from(this.children).forEach((child, i) => {
      const h = child as HTMLElement
      if (card) {
        const active = i === index
        h.style.opacity = active ? '' : '0.45'
        h.style.transform = active ? '' : 'scale(var(--oas-carousel-card-scale, 0.85))'
        h.style.zIndex = active ? '2' : '1'
      } else if (fade) {
        h.style.opacity = i === index ? '1' : '0'
        h.style.pointerEvents = i === index ? '' : 'none'
      } else if (h.style.opacity !== '' || h.style.transform !== '' || h.style.zIndex !== '') {
        h.style.opacity = ''
        h.style.transform = ''
        h.style.zIndex = ''
        h.style.pointerEvents = ''
      }
    })

    // 箭头显示形态：always（始终显示）/ hover（悬停显示，默认）/ never（不显示）
    const arrows = this.getAttr('arrows', 'hover')
    this.shadow.querySelector('[part="arrow-prev"]')?.toggleAttribute('hidden', arrows === 'never')
    this.shadow.querySelector('[part="arrow-next"]')?.toggleAttribute('hidden', arrows === 'never')
    // 非循环模式下边界箭头禁用
    const loop = this.getAttr('loop', '') !== 'false'
    this.shadow.querySelector('[part="arrow-prev"]')?.toggleAttribute('disabled', !loop && index === 0)
    this.shadow.querySelector('[part="arrow-next"]')?.toggleAttribute('disabled', !loop && index === pages - 1)
    // 箭头/指示器内置文案走 locale registry（setLocale 切换自动刷新）
    this.shadow.querySelector<HTMLElement>('[part="arrow-prev"]')?.setAttribute('aria-label', this.t('carousel.prev'))
    this.shadow.querySelector<HTMLElement>('[part="arrow-next"]')?.setAttribute('aria-label', this.t('carousel.next'))

    // 显式暂停按钮：pause-button 开启时显示；aria-pressed 与文案同步暂停态
    const pauseBtn = this.shadow.querySelector<HTMLElement>('[part="pause-button"]')
    if (pauseBtn) {
      const show = this.hasAttr('pause-button')
      pauseBtn.toggleAttribute('hidden', !show)
      if (show) {
        pauseBtn.setAttribute('aria-pressed', String(this.userPaused))
        pauseBtn.setAttribute('aria-label', this.t(this.userPaused ? 'carousel.play' : 'carousel.pause'))
      }
    }

    // 视口 aria-live 策略：自动播放时 off（读屏不逐屏播报），否则 polite
    this.shadow
      .querySelector('[part="viewport"]')
      ?.setAttribute('aria-live', this.hasAttr('autoplay') ? 'off' : 'polite')

    // 指示器：indicators=false 隐藏；position=outside 流内占位；
    // thumbs 开启时渲染缩略图条替代圆点；否则 type=line 线性形态
    const dots = this.shadow.querySelector<HTMLElement>('[part="dots"]')
    if (!dots) return
    const showDots = this.getAttr('indicators', 'true') !== 'false'
    const thumbsOn = this.thumbsEnabled()
    dots.toggleAttribute('hidden', !showDots)
    dots.classList.toggle('outside', this.getAttr('indicator-position', 'inside') === 'outside')
    dots.classList.toggle('thumbs', thumbsOn)
    dots.classList.toggle('line', !thumbsOn && this.getAttr('indicator-type', 'dot') === 'line')
    dots.innerHTML = ''
    if (!showDots) return
    if (thumbsOn) {
      const sources = this.thumbSources(pages)
      for (let i = 0; i < pages; i++) {
        const thumb = document.createElement('button')
        thumb.className = 'thumb'
        thumb.setAttribute('part', 'thumb')
        thumb.setAttribute('role', 'tab')
        thumb.setAttribute('aria-current', String(i === index))
        thumb.setAttribute('aria-label', this.t('carousel.dot', { index: i + 1 }))
        thumb.setAttribute('data-index', String(i))
        const src = sources[i]
        if (src) {
          const img = document.createElement('img')
          img.setAttribute('src', src)
          img.setAttribute('alt', '')
          thumb.appendChild(img)
        } else {
          const ph = document.createElement('span')
          ph.className = 'thumb-placeholder'
          ph.setAttribute('aria-hidden', 'true')
          ph.textContent = String(i + 1)
          thumb.appendChild(ph)
        }
        dots.appendChild(thumb)
      }
      this.schedule()
      return
    }
    for (let i = 0; i < pages; i++) {
      const dot = document.createElement('button')
      dot.className = 'dot'
      dot.setAttribute('part', 'dot')
      dot.setAttribute('role', 'tab')
      dot.setAttribute('aria-current', String(i === index))
      dot.setAttribute('aria-label', this.t('carousel.dot', { index: i + 1 }))
      dot.setAttribute('data-index', String(i))
      dots.appendChild(dot)
    }
    this.schedule()
  }

  /** thumbs 是否开启（布尔属性族：存在即真，仅显式 "false" 关闭；JSON 数组值同样视为开启） */
  private thumbsEnabled(): boolean {
    if (!this.hasAttr('thumbs')) return false
    return this.getAttr('thumbs', '') !== 'false'
  }

  /**
   * 缩略图数据源（按页）：优先 thumbs 属性 JSON 数组，其次子项 data-thumb，
   * 再次子项内 <img> 的 src，均无则 null（渲染序号占位）。
   */
  private thumbSources(pages: number): (string | null)[] {
    const raw = this.getAttr('thumbs', '')
    let arr: string[] | null = null
    if (raw && raw !== 'true') {
      try {
        const parsed: unknown = JSON.parse(raw)
        if (Array.isArray(parsed)) arr = parsed.map((v) => String(v))
      } catch {
        /* 非 JSON 数组：回落到子项取图 */
      }
    }
    const kids = Array.from(this.children)
    const per = this.perView()
    const out: (string | null)[] = []
    for (let i = 0; i < pages; i++) {
      const fromArr = arr && arr[i] ? arr[i]! : ''
      if (fromArr) {
        out.push(fromArr)
        continue
      }
      // 每屏多张时，取该页首张子项作为缩略图源
      const child = kids[Math.min(i * per, kids.length - 1)] as HTMLElement | undefined
      const dataThumb = child?.getAttribute?.('data-thumb')
      if (dataThumb) {
        out.push(dataThumb)
        continue
      }
      // 子项自身是 <img> 时取其 src；否则取子项内首个 <img>
      const img = child?.tagName === 'IMG' ? child : child?.querySelector?.('img')
      out.push(img?.getAttribute?.('src') ?? null)
    }
    return out
  }

  /** 切换到目标页（循环取模 / 非循环收敛），同页 no-op；切换派发 oas-change（含 prevIndex） */
  private goto(index: number): void {
    const pages = this.pageCount()
    if (pages === 0) return
    const current = this.current()
    let next: number
    if (this.getAttr('loop', '') !== 'false') {
      next = ((index % pages) + pages) % pages
    } else {
      next = Math.min(pages - 1, Math.max(0, index))
    }
    if (next === current) return
    this.setAttribute('index', String(next))
    this.emit('change', { index: next, prevIndex: current })
    // attributeChangedCallback 已同步 update()，此处无需重复调用
  }

  /** 切换到下一屏 */
  next(): void {
    this.goTo(this.current() + 1)
  }

  /** 切换到上一屏 */
  prev(): void {
    this.goTo(this.current() - 1)
  }

  /** 跳转到指定屏（页索引，从 0 起） */
  goTo(index: number): void {
    this.goto(index)
  }

  /** 自动播放是否处于暂停（显式暂停 > 悬停/聚焦/不可见/拖拽，任一命中即暂停） */
  private isPaused(): boolean {
    if (this.userPaused) return true
    if (this.dragPaused || this.hiddenPaused) return true
    if (!this.hasAttr('autoplay')) return false
    if (this.getAttr('pause-on-hover', 'true') === 'false') return false
    return this.hoverPaused || this.focusPaused
  }

  private schedule(): void {
    if (this.timer) clearInterval(this.timer)
    this.timer = null
    if (!this.hasAttr('autoplay')) return
    this.timer = setInterval(
      () => {
        if (!this.isPaused()) this.goTo(this.current() + 1)
      },
      Number(this.getAttr('interval', '3000')) || 3000,
    )
  }
}

function trackOf(el: OASCarousel): HTMLElement | null {
  return el.shadowRoot?.querySelector<HTMLElement>('[part="track"]') ?? null
}
